import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Button, CircularProgress, IconButton, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as CalculationHelper from '../api/calculation/calculationHelper';
import i18n from '../localization';
import Span from '../shared/component/Span';
import { EPropellerDirectionWithRespectToTheQuay, ETypeOfPropeller } from '../shared/enum';
import { IEngine, IScenario } from '../shared/iProject';
import { cleanAlert, selectAlert } from '../store/alert/alertSlice';
import { cleanEngine } from '../store/engine/engineSlice';
import { EReduxState } from '../store/enum';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { cleanMap } from '../store/map/mapSlice';
import { newEngine, newScenario, removeEngine, removeScenario, selectProject } from '../store/project/projectSlice';
import { cleanResult, selectResult, setResult } from '../store/result/resultSlice';
import { selectUser } from '../store/user/userSlice';
import CommonResult from './CommonResult';
import Scenario from './scenario/Index';

interface IMultiScenarioProps {
    withInitialCalculation?: boolean;
}

export const CurrentScenarioContext = React.createContext<number>(0);

export const getNameScenario = (x: IScenario, engine: IEngine) => {
    let arrName = [x.vesselType];
    arrName.push(engine.numberOfPropellers.toString() + ' ' + `${engine.typeOfPropeller == ETypeOfPropeller.MainPropeller ? i18n.t('mainPropeller') : i18n.t('bowThruster')}`);
    arrName.push(`${engine.propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Parallel ? i18n.t('parallel') : i18n.t('transversal')}`);
    return arrName.join(' - ');
};

const MultiScenario = (props: IMultiScenarioProps) => {
    const [indexScenario, setIndexScenario] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);

    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);
    const result = useAppSelector(selectResult);
    const alert = useAppSelector(selectAlert);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (props.withInitialCalculation) handleClickRun();
    }, []);

    const handleChange = (event: React.SyntheticEvent, newValue: string) => setIndexScenario(parseInt(newValue));

    const addScenario = () => {
        dispatch(cleanResult());
        dispatch(cleanAlert());
        dispatch(cleanEngine());
        dispatch(cleanMap());
        dispatch(newScenario());
        dispatch(newEngine());
        setIndexScenario(project.data!.scenarios!.length!);
    };

    const deleteScenario = (index: number) => {
        dispatch(cleanResult());
        dispatch(cleanAlert());
        dispatch(cleanEngine());
        dispatch(cleanMap());
        dispatch(removeScenario(index));
        dispatch(removeEngine(index));
        if (indexScenario == 100) {
            setIndexScenario(0);
        } else {
            if (index == indexScenario) setIndexScenario(indexScenario - 1);
        }
    };

    const handleClickRun = async () => {
        setIsLoading(true);
        let res = await CalculationHelper.getAllCalculatedScenariosAsync(project.data!, user.data?.email!);
        setIsLoading(false);
        dispatch(setResult(res!.data));
    };

    return (
        <>
            {isLoading && <CircularProgress />}
            {!isLoading && (
                <Box sx={{ width: '100%', typography: 'body1' }} height={'100%'}>
                    <TabContext value={indexScenario.toString()}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Stack direction={'row'} justifyContent={'space-between'} padding={'0 1rem'}>
                                <Stack direction={'row'} sx={{ width: { xs: '55%', sm: '70%', md: '80%', lg: 'auto' } }}>
                                    <TabList variant='scrollable' onChange={handleChange} aria-label='Scenario tab'>
                                        {project.data?.scenarios!.map((x, i) => {
                                            if (i == 0) {
                                                return [
                                                    <Tab
                                                        key={`${i}_tab`}
                                                        variant={alert.data?.indexScenarioError.some(x => x == i) ? 'warning' : 'standard'}
                                                        label={getNameScenario(x, project.data!.engines![i])}
                                                        value={i.toString()}
                                                    />,
                                                ];
                                            } else {
                                                return [
                                                    <Tab
                                                        key={`${i}_tab`}
                                                        variant={alert.data?.indexScenarioError.some(x => x == i) ? 'warning' : 'standard'}
                                                        label={getNameScenario(x, project.data!.engines![i])}
                                                        value={i.toString()}
                                                    />,
                                                    <IconButton aria-label='delete' key={`${i}_div`} onClick={() => deleteScenario(i)}>
                                                        <CloseIcon fontSize='small' />
                                                    </IconButton>,
                                                ];
                                            }
                                        })}
                                        <IconButton aria-label='add' onClick={addScenario}>
                                            <AddCircleIcon color='orange' fontSize='large' />
                                        </IconButton>
                                    </TabList>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'end'}>
                                    {result.status == EReduxState.fulfilled && (
                                        <TabList onChange={handleChange} aria-label='Scenario tab'>
                                            <Tab variant='standard' label={t('result')} value='100' />
                                        </TabList>
                                    )}
                                    <Button variant='green' startIcon={<PlayArrowIcon color='orange' fontSize='small' />} onClick={handleClickRun}>
                                        <Span text={t('run')} />
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                        <CurrentScenarioContext.Provider value={indexScenario}>
                            {project.data?.scenarios!.map((x, i) => (
                                <TabPanel key={`${i}_panel`} value={i.toString()} style={{ height: '90%' }}>
                                    <Scenario />
                                </TabPanel>
                            ))}
                        </CurrentScenarioContext.Provider>
                        <TabPanel key='results_panel' value='100' style={{ height: '85%' }}>
                            <Stack height={'100%'} position={'relative'}>
                                <CommonResult />
                            </Stack>
                        </TabPanel>
                    </TabContext>
                </Box>
            )}
        </>
    );
};

export default MultiScenario;
